
import React, { useEffect, useRef, useState } from "react";
import { Star, Quote } from "lucide-react";

const TocTocTestimonials = () => {
  const testimonials = [
    {
      name: "Ana Carolina",
      role: "Investidora",
      image: "/lovable-uploads/f0d16a3e-d745-4bd8-8b3e-143d9e9a6bdc.png",
      rating: 5,
      text: "Consegui vender meu imóvel 20% acima do valor inicial graças às fotos profissionais. Investimento que vale muito a pena!",
      highlight: "+20% valor de venda"
    },
    {
      name: "Eduardo Mendes",
      role: "Corretor de Imóveis",
      image: "/lovable-uploads/7f6fb478-518d-43c6-a635-15c5b9f11767.png",
      rating: 5,
      text: "Trabalho com a Toc Toc há 2 anos. A qualidade é excepcional e meus clientes sempre ficam impressionados com os resultados.",
      highlight: "Parceiro há 2 anos"
    },
    {
      name: "Sofia Silva",
      role: "Proprietária",
      image: "/lovable-uploads/62c14a64-1853-42f2-ba62-94ef70302752.png",
      rating: 5,
      text: "As fotos ficaram incríveis! Meu apartamento nunca pareceu tão bonito. Recebi várias propostas em menos de uma semana.",
      highlight: "Vendeu em 1 semana"
    }
  ];

  // Counter animation hooks - start at 0 to prevent hydration mismatch
  const [countImoveis, setCountImoveis] = useState(0);
  const [countSatisfeitos, setCountSatisfeitos] = useState(0);
  const [countVenda, setCountVenda] = useState(0);
  const [countValor, setCountValor] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // Initialize client-side state
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Counter animation effect
  useEffect(() => {
    if (!isClient || hasAnimated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          // Defer animations to improve TTI
          setTimeout(() => {
            // Animate Imóveis counter to 30000
            const imoveisInterval = setInterval(() => {
              setCountImoveis(prev => {
                if (prev >= 30000) {
                  clearInterval(imoveisInterval);
                  return 30000;
                }
                return prev + 1000;
              });
            }, 50);

            // Animate Satisfeitos counter to 98
            const satisfeitosInterval = setInterval(() => {
              setCountSatisfeitos(prev => {
                if (prev >= 98) {
                  clearInterval(satisfeitosInterval);
                  return 98;
                }
                return prev + 3;
              });
            }, 60);

            // Animate Venda counter to 50
            const vendaInterval = setInterval(() => {
              setCountVenda(prev => {
                if (prev >= 50) {
                  clearInterval(vendaInterval);
                  return 50;
                }
                return prev + 2;
              });
            }, 80);

            // Animate Valor counter to 30
            const valorInterval = setInterval(() => {
              setCountValor(prev => {
                if (prev >= 30) {
                  clearInterval(valorInterval);
                  return 30;
                }
                return prev + 1;
              });
            }, 100);
          }, 400); // Defer by 400ms to improve TTI
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, isClient]);

  return (
    <section className="py-16 md:py-24 bg-[#F6E5C9]/20">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="text-center mb-16 animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
          <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-6">
            O que nossos <span className="text-[#FF5605]">clientes</span> dizem
          </h2>
          <p className="text-lg text-[#5B5A59] max-w-3xl mx-auto">
            Mais de 30 mil clientes satisfeitos que transformaram seus imóveis 
            em apresentações irresistíveis.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className={`testimonial-${index + 1} bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group relative animate-[fadeInUp_0.8s_ease-out_${0.2 + (index * 0.3)}s_both]`}
            >
              <Quote className="h-8 w-8 text-[#FF5605]/20 mb-6 animate-[pulse_2s_infinite]" />
              
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-[#FF5605] fill-current animate-[pulse_1s_infinite]" style={{animationDelay: `${i * 0.1}s`}} />
                ))}
              </div>
              
              <p className="text-[#5B5A59] leading-relaxed mb-6 text-lg">
                "{testimonial.text}"
              </p>
              
              <div className="flex items-center">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name}
                  loading="lazy"
                  width="48"
                  height="48"
                  className="w-12 h-12 rounded-full object-cover mr-4 group-hover:scale-110 transition-transform duration-300"
                />
                <div>
                  <h3 className="font-bold text-[#323031]">{testimonial.name}</h3>
                  <p className="text-sm text-[#5B5A59]">{testimonial.role}</p>
                </div>
              </div>
              
              <div className="absolute top-4 right-4 bg-[#FF5605]/10 text-[#FF5605] px-3 py-1 rounded-full text-xs font-medium animate-[bounce_2s_infinite]">
                {testimonial.highlight}
              </div>
            </div>
          ))}
        </div>
        
        <div ref={statsRef} className="bg-white rounded-2xl p-8 shadow-lg animate-[fadeInUp_0.8s_ease-out_1s_both]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="result-box">
              <div className="text-3xl font-bold text-[#FF5605] mb-2 counter-animation animate-[pulse_2s_infinite]">
                {countImoveis > 1000 ? `${Math.floor(countImoveis/1000)}k+` : `${countImoveis}+`}
              </div>
              <div className="text-sm text-[#5B5A59]">Imóveis fotografados</div>
            </div>
            <div className="result-box">
              <div className="text-3xl font-bold text-[#3332FC] mb-2 counter-animation animate-[pulse_2s_infinite_0.3s]">
                {countSatisfeitos}%
              </div>
              <div className="text-sm text-[#5B5A59]">Clientes satisfeitos</div>
            </div>
            <div className="result-box">
              <div className="text-3xl font-bold text-[#FF5605] mb-2 counter-animation animate-[pulse_2s_infinite_0.6s]">
                {countVenda}%
              </div>
              <div className="text-sm text-[#5B5A59]">Venda mais rápida</div>
            </div>
            <div className="result-box">
              <div className="text-3xl font-bold text-[#3332FC] mb-2 counter-animation animate-[pulse_2s_infinite_0.9s]">
                {countValor}%
              </div>
              <div className="text-sm text-[#5B5A59]">Maior valorização</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TocTocTestimonials;
